import React, { useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, ShieldCheck, Truck } from "lucide-react";

function B2BCartPage() {
  const navigate = useNavigate();
  const brand = useMemo(
    () => ({
      accent: "#0EA5E9",
      accent2: "#22C55E",
      bg: "#F7FAFC",
      text: "#0F172A",
      subtext: "rgba(15, 23, 42, 0.72)",
      stroke: "rgba(2, 6, 23, 0.10)",
    }),
    []
  );

  const items = useMemo(
    () => [
      {
        id: 3,
        title: "Packaging Tape (Case)",
        desc: "72mm • acrylic adhesive • brown",
        price: 80,
        qty: 24,
        moq: 24,
        lead: "Same day",
        image:
          "https://images.unsplash.com/photo-1586528116493-92a1f0a4f8b0?w=200&h=200&fit=crop",
      },
      {
        id: 1,
        title: "Safety Helmet (Bulk Pack)",
        desc: "IS certified • adjustable • yellow",
        price: 150,
        qty: 10,
        moq: 10,
        lead: "2-3 days",
        image:
          "https://images.unsplash.com/photo-1603570419989-83d79d7e9eaa?w=200&h=200&fit=crop",
      },
    ],
    []
  );

  const subtotal = useMemo(
    () => items.reduce((sum, x) => sum + x.price * x.qty, 0),
    [items]
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: brand.bg, py: 6 }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: `1px solid ${brand.stroke}`,
            backgroundImage:
              'linear-gradient(90deg, rgba(14,165,233,0.12), rgba(34,197,94,0.10)), url("https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1400&h=320&fit=crop")',
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h4" sx={{ fontWeight: 950, color: brand.text }}>
              B2B Cart
            </Typography>
            <Typography sx={{ color: brand.subtext, mt: 0.5 }}>
              Review quantities, MOQs, and proceed to checkout.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
              <Chip icon={<ShieldCheck size={16} />} label="MOQ validation" />
              <Chip icon={<Truck size={16} />} label="Lead time shown" />
            </Stack>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Typography sx={{ fontWeight: 950, color: brand.text, mb: 2 }}>
                  Items
                </Typography>
                <Stack spacing={1.25}>
                  {items.map((x) => (
                    <Box
                      key={x.id}
                      sx={{
                        p: 1.75,
                        borderRadius: 3,
                        border: `1px solid ${brand.stroke}`,
                        bgcolor: "white",
                      }}
                    >
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                          <Avatar variant="rounded" src={x.image} sx={{ width: 56, height: 56 }} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 950, color: brand.text }} noWrap>
                              {x.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: brand.subtext }} noWrap>
                              {x.desc}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                              <Chip size="small" label={`MOQ ${x.moq}`} />
                              <Chip size="small" label={`Lead: ${x.lead}`} />
                              <Chip size="small" label={`₹${x.price} each`} />
                            </Stack>
                          </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ minWidth: 36, borderRadius: 2.5, borderColor: brand.stroke }}
                          >
                            <Minus size={16} />
                          </Button>
                          <Typography sx={{ fontWeight: 950, color: brand.text, minWidth: 32, textAlign: "center" }}>
                            {x.qty}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ minWidth: 36, borderRadius: 2.5, borderColor: brand.stroke }}
                          >
                            <Plus size={16} />
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/B2BShop")}
                    sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 950 }}
                  >
                    Continue shopping
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/B2BCheckout")}
                    sx={{
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 950,
                      color: "white",
                      background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                    }}
                  >
                    Checkout
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Typography sx={{ fontWeight: 950, color: brand.text }}>
                  Order summary
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Subtotal
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 950, color: brand.text }}>
                      ₹{subtotal.toLocaleString()}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Shipping
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 950, color: brand.text }}>
                      Calculated at checkout
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Taxes (GST)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 950, color: brand.text }}>
                      As per invoice
                    </Typography>
                  </Stack>
                </Stack>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => navigate("/B2BCheckout")}
                  sx={{
                    mt: 2,
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 950,
                    color: "white",
                    background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                  }}
                >
                  Proceed to checkout
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate("/B2BRFQ")}
                  sx={{
                    mt: 1,
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 950,
                    borderColor: brand.stroke,
                    color: brand.text,
                  }}
                >
                  Request a quote instead
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default B2BCartPage;

